import { wysiwygStyles } from './Text.styled';

import { ComponentType } from 'react';

import { css, Interpolation, Theme } from '@emotion/react';
import { CSSInterpolation } from '@emotion/serialize';
import styled from '@emotion/styled';
import { ErrorMsgs, Input } from '@hangar31/rc-form';
import { mq } from '@hangar31/rc-layout';
import toColor, { ColorInput } from '@OnArk/utils/toColor';
import ReactDatePicker, { DatePickerProps } from 'react-datepicker';

export const ErrorMsgsStyled = styled(ErrorMsgs)(
  ({ theme }) => css`
    padding-left: 2rem;

    color: ${theme.colors.grayDark};

    vertical-align: middle;

    list-style: none;

    li {
      position: relative;

      &:before {
        content: '';

        position: absolute;
        top: 0.2rem;
        left: -2rem;

        width: 1.5rem;
        height: 1.5rem;

        background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M17.5 10.1511C17.5 14.2933 14.1421 17.6511 10 17.6511C5.85786 17.6511 2.5 14.2933 2.5 10.1511C2.5 6.00898 5.85786 2.65112 10 2.65112C14.1421 2.65112 17.5 6.00898 17.5 10.1511ZM11.3306 5.94153L10.7588 10.9737C10.715 11.3596 10.3884 11.6511 10 11.6511C9.6116 11.6511 9.28505 11.3596 9.24121 10.9737L8.66937 5.94153C8.5791 5.14713 9.20049 4.45112 10 4.45112C10.7995 4.45112 11.4209 5.14713 11.3306 5.94153ZM10 15.8511C10.8284 15.8511 11.5 15.1795 11.5 14.3511C11.5 13.5227 10.8284 12.8511 10 12.8511C9.17156 12.8511 8.5 13.5227 8.5 14.3511C8.5 15.1795 9.17156 15.8511 10 15.8511Z' fill='%23585D64'/%3E%3C/svg%3E%0A");
        background-size: contain;
      }
    }
  `
);

export const LabelTextStyled = styled.label<{ lower?: boolean }>(
  ({ theme }) => theme.font('xs'),
  ({ theme }) => css`
    display: block;
    margin: 1rem 0 0 0;

    color: ${theme.colors.grayMed};
    text-transform: uppercase;
    em {
      color: ${toColor(theme.church.buttonColor)};
    }
  `,
  ({ lower }) =>
    lower &&
    css`
      text-transform: none;
    `
);

export const FieldHintStyled = styled.span(
  ({ theme }) => theme.font('xs'),
  ({ theme }) => css`
    display: block;
    margin-top: 0.4rem;

    color: ${theme.colors.grayMed};
  `
);

export const LabelStyled = styled.label<{ lower?: boolean }>(
  ({ theme }) => theme.font('xs'),
  ({ theme }) => ({
    a: theme.font('xs'),
  }),
  ({ theme }) => css`
    position: relative;

    display: block;

    span:first-of-type {
      display: block;
      margin-bottom: 0.8rem;

      color: ${theme.colors.grayMed};
      text-transform: uppercase;

      em {
        color: ${toColor(theme.church.buttonColor)};
      }
    }

    a {
      float: right;
    }
    &[aria-invalid='true'] input,
    &[aria-invalid='true'] select,
    &[aria-invalid='true'] textarea,
    .StripeElement--invalid {
      border-color: ${toColor(theme.church.buttonColor) ||
      theme.colors.highlight};
    }

    &[aria-invalid] input,
    &[aria-invalid] select,
    &[aria-invalid] textarea {
      &:focus {
        border-color: ${theme.colors.grayLight};
      }
    }
  `,
  ({ lower }) =>
    lower &&
    css`
      span:first-of-type {
        text-transform: none;
      }
    `
);

export const ToggleLabelStyled = styled(LabelStyled)(
  () => css`
    display: flex;
    align-items: center;
    margin: 1rem 1.3rem 0 0;

    text-transform: none;
  `
);
export const ToggleStyled = styled.input(
  ({ theme }) => css`
    position: relative;

    width: 3em;
    height: 1.4em;
    margin: 0 0.8rem 0 0;

    background: transparent;
    border: 1px solid ${theme.colors.clickable};
    border-radius: 100px;
    outline: none;
    cursor: pointer;

    transition: all 0.5s;

    appearance: none;

    &:checked {
      background: ${theme.colors.clickableLight};
      border-color: ${theme.colors.clickableLight};
    }
    &::before {
      position: absolute;
      top: -1px;
      left: -1px;

      width: 1.2em;
      height: 1.2em;

      background: ${theme.colors.whiteDark};
      border: 1px solid ${theme.colors.clickable};
      border-radius: 1em;
      box-shadow: ${theme.shadows.sm};
      transform: scale(1.1);

      transition: all 0.5s;
      content: '';
    }
    &:checked::before {
      left: 1.6em;

      background: ${theme.colors.clickable};
    }

    &:disabled {
      &:checked {
        background-color: ${theme.colors.grayLight};
        border-color: ${theme.colors.grayLight};
      }
      border-color: ${theme.colors.grayMed};
      &:before {
        background-color: ${theme.colors.grayMed};
        border-color: ${theme.colors.grayMed};
      }
    }
  `
);

export const RemoveInputStyled = styled.div(
  () => css`
    position: relative;

    input {
      width: 100%;
      padding-right: 5rem;
    }
    button {
      position: absolute;
      top: 1rem;
      right: 0.5rem;
    }
  `
);

// The prop surface the shared input interpolations actually read: every
// consumer (styled(Input), styled.input, styled.div, the DatePicker) provides
// `theme`, and only DefaultInputStyled passes `sm`. NOTE: this must be the
// augmented emotion Theme, not OnArkTheme — @hangar31/rc-layout also augments
// Theme (H31Layout) with optional members, so the two aren't interchangeable.
interface InputStyleProps {
  theme: Theme;
  sm?: boolean;
}

const inputStyles: Interpolation<InputStyleProps>[] = [
  ({ theme }: InputStyleProps) => theme.font('sm'),
  ({ theme }: InputStyleProps) => ({
    "&[type='radio'] + label, &[type='checkbox'] + label": theme.font('sm'),
  }),
  ({ theme }: InputStyleProps) => css`
    display: block;
    width: 100%;
    padding: 0.8rem;

    color: ${theme.colors.blackLight};

    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.border};
    border-radius: 0.4rem;
    &select {
      cursor: pointer;
    }

    &[type='color'] {
      height: 4.4rem;
    }
    &[type='file'] {
      font-size: 1.4rem;
    }
    &textarea {
      min-height: 20rem;
    }
    &::placeholder {
      color: ${theme.colors.grayMed};
    }
    &:focus {
      border-color: ${theme.colors.grayLight};
    }
    &[disabled] {
      color: ${theme.colors.grayMed};

      background-color: ${theme.colors.disabled};
    }

    &[type='radio'] + label:before {
      border-radius: 50%;
    }

    &[type='radio'],
    &[type='checkbox'] {
      width: 0;
      height: 0;
      margin: 0;
      padding: 0;

      visibility: hidden;

      + label {
        display: flex;
        padding: 0.8rem 0;

        line-height: 1em;

        vertical-align: -14px;

        cursor: pointer;
        span {
          margin-bottom: 0;
        }

        &:before {
          display: block;

          box-sizing: border-box;
          width: 1.2em;
          min-width: 1.2em;
          height: 1.2em;
          margin-right: 0.8em;

          vertical-align: middle;

          background: ${theme.colors.whiteDark};

          border: 1px solid ${theme.colors.border};
          content: '';
        }
      }

      &[type='radio']:checked + label:before {
        border-width: 0.35em;
        border-color: ${toColor(theme.church.buttonColor)};
      }
      &[type='checkbox']:checked + label:before {
        border-width: 0.6em;
        border-color: ${toColor(theme.church.buttonColor)};
      }
    }
  `,

  ({ sm }: InputStyleProps) =>
    sm &&
    css`
      &[type='checkbox'] {
        + label {
          padding: 0.4rem 0;

          font-size: 1.4rem;
          vertical-align: -14px;

          &:before {
            width: 1.4rem;
            min-width: 1.4rem;
            height: 1.4rem;
            margin-right: 1.4rem;
            overflow: hidden;
          }
        }

        &[type='checkbox']:checked + label:before {
          border-width: 0.7rem;
        }
      }
    `,
];

export const InputStyled = styled(Input)<{
  options?: Record<string, any>;
}>(...inputStyles);
// Cast around react-datepicker v9's `defaultProps` typing (its `customTimeInput`
// default trips Emotion's styled() component-type constraint); DatePickerProps
// keeps the consumer prop typing.
export const DatePickerStyled = styled(
  ReactDatePicker as unknown as ComponentType<DatePickerProps>
)<{
  options?: Record<string, any>;
}>(...inputStyles);
export const DefaultInputStyled = styled.input<{
  options?: Record<string, any>;
  sm?: boolean;
}>(...inputStyles);

export const AutosuggestStyled = styled.div(
  ({ theme }) => ({
    '.react-autosuggest__suggestion ': theme.font('sm'),
    // The interpolation results (CSSObject / SerializedStyles / mq() arrays)
    // are all CSSInterpolation; the function members have been invoked so the
    // wider Interpolation type (which allows functions) no longer applies.
    input: inputStyles.map(fn => {
      if (typeof fn === 'function') return fn({ theme }) as CSSInterpolation;
    }),
  }),
  ({ theme }) => css`
    position: relative;
    z-index: 100;
    .react-autosuggest__input--open {
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }
    .react-autosuggest__suggestions-container--open {
      position: absolute;
      top: 100%;
      z-index: 2;

      display: block;
      width: 100%;
      margin-top: -1px;
      overflow: hidden;

      font-weight: 300;
      font-size: 16px;
      font-family: ${theme.fontFamilies.Roboto};

      background-color: ${theme.colors.white};
      border: 1px solid ${toColor(theme.colors.clickable, 0.5)};
      border-bottom-right-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    .react-autosuggest__suggestions-list {
      margin: 0;
      padding: 0;

      list-style-type: none;
    }

    .react-autosuggest__suggestion {
      padding: 10px 20px;

      cursor: pointer;
    }

    .react-autosuggest__suggestion--highlighted {
      background-color: ${theme.colors.whiteHover};
    }
  `
);

export const EditorStyled = styled.div(
  ...wysiwygStyles,
  ({ theme }) => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    margin-top: 0;

    color: ${theme.colors.black};

    background-color: white;
    border-radius: 0.4rem;

    .ql-container {
      ${theme.font('sm')}
      border-color: ${theme.colors.border};
      border-radius: 0 0 0.4rem 0.4rem;
    }

    .ql-container.ql-snow {
      height: 275px;

      border-radius: 0 0 0.4rem 0.4rem;
    }

    .ql-editor {
      height: 100%;
      min-height: 20rem;
    }

    .ql-toolbar {
      border-color: ${theme.colors.border};
      border-radius: 0.4rem 0.4rem 0 0;
      .ql-stroke {
        stroke: ${theme.colors.grayLight};
      }
      .ql-fill,
      .ql-fill.ql-stroke {
        fill: ${theme.colors.grayLight};
      }
      .ql-fill button:hover,
      .ql-fill button.ql-active,
      .ql-fill.ql-strokebutton:hover,
      .ql-fill.ql-stroke button.ql-active {
        .ql-stroke {
          stroke: ${theme.colors.black};
        }
        .ql-fill {
          fill: ${theme.colors.black};
        }
      }
    }
  `
);

export const EditorSmallStyled = styled.div(
  ...wysiwygStyles,
  ({ theme }) => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    margin-top: 0;

    color: ${theme.colors.black};

    background-color: white;
    border-radius: 0.4rem;

    .ql-container {
      ${theme.font('sm')}
      border-color: ${theme.colors.border};
      border-radius: 0 0 0.4rem 0.4rem;
    }

    .ql-container.ql-snow {
      height: 150px;

      border-radius: 0 0 0.4rem 0.4rem;
    }

    .ql-editor {
      height: 100%;
      min-height: 20rem;
    }

    .ql-toolbar {
      border-color: ${theme.colors.border};
      border-radius: 0.4rem 0.4rem 0 0;
      .ql-stroke {
        stroke: ${theme.colors.grayLight};
      }
      .ql-fill,
      .ql-fill.ql-stroke {
        fill: ${theme.colors.grayLight};
      }
      .ql-fill button:hover,
      .ql-fill button.ql-active,
      .ql-fill.ql-strokebutton:hover,
      .ql-fill.ql-stroke button.ql-active {
        .ql-stroke {
          stroke: ${theme.colors.black};
        }
        .ql-fill {
          fill: ${theme.colors.black};
        }
      }
    }
  `
);

export const SectionButtonsStyled = styled.nav(
  () => css`
    display: flex;
    justify-content: center;
    width: max-content;
    margin: 6rem auto 3rem;

    button,
    a {
      &[aria-label='back'] {
        margin-right: 1rem;
        margin-left: -5.3rem;
      }
      &[aria-label='next'] {
        padding: 1.2rem 4.7rem;
      }
    }
  `
);

export const SectionBoxStyled = styled.article<{
  bannerColor?: ColorInput;
  preview?: boolean;
  bannerStyle?: BannerStyle;
}>(
  () => css`
    min-height: 100vh;

    background-repeat: repeat no-repeat;
    background-position: bottom right;

    transition: all 0.2s;
  `,
  ({ bannerColor, theme, bannerStyle = theme.church.bannerStyle }) => {
    const b = toColor(bannerColor ?? theme.church.bannerColor);

    if (bannerStyle === 'TRIANGLES')
      return css`
        background-image: url("data:image/svg+xml,%3Csvg width='400' height='200' viewBox='0 0 400 200' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath opacity='0.15' d='M394 200C397.314 200 400 197.378 400 194.065C400 169.919 400 0 400 0L176 27.4097L0 2.2258e-06L7.62939e-06 194C7.77424e-06 197.314 2.6863 200 6.00001 200H394Z' fill='${b}' /%3E%3Cpath opacity='0.15' d='M6.00001 199.999C2.6863 199.999 9.77695e-06 197.313 9.53674e-06 193.999L0 50.69L100 37.2539L280 68.4201L400 50.6953V193.999C400 197.313 397.314 199.999 394 199.999H6.00001Z' fill='${b}' /%3E%3Cpath opacity='0.5' d='M394 200C397.314 200 400 197.314 400 194V94.4846L313.5 88.0977L114 105.767L0 94.4846L3.8147e-06 194C3.95954e-06 197.314 2.68629 200 6 200H394Z' fill='${b}' /%3E%3Cpath d='M1.90735e-06 200.001L0 134.752L92 127.361L264 151.957L400 134.932V200.001H1.90735e-06Z' fill='${b}' /%3E%3C/svg%3E%0A");
      `;
    if (bannerStyle === 'LINES')
      return css`
        background-image: url("data:image/svg+xml,%3Csvg width='400' height='200' viewBox='0 0 400 200' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath opacity='0.15' d='M394 200C397.314 200 400 197.378 400 194.065C400 169.919 400 0 400 0L0 1.90735e-06L7.62939e-06 194C7.77424e-06 197.314 2.6863 200 6.00001 200H394Z' fill='${b}'/%3E%3Cpath opacity='0.15' d='M6.00001 200.001C2.6863 200.001 9.77695e-06 197.315 9.53674e-06 194.001L0 50.6914L400 50.6967V194.001C400 197.315 397.314 200.001 394 200.001H6.00001Z' fill='${b}'/%3E%3Cpath opacity='0.5' d='M394 200C397.314 200 400 197.314 400 194V94.4844H0L3.8147e-06 194C3.95954e-06 197.314 2.68629 200 6 200H394Z' fill='${b}'/%3E%3Cpath d='M1.90735e-06 200.001L0 134.752L400 134.932V200.001H1.90735e-06Z' fill='${b}'/%3E%3C/svg%3E%0A");
      `;
    if (bannerStyle === 'NONE')
      return mq({ padding: '3rem 3rem 10rem !important' });

    return css`
      background-image: url("data:image/svg+xml,%3Csvg width='400' height='200' viewBox='0 0 400 200' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M394 200C397.314 200 400 197.378 400 194.065C400 169.919 400 76.7049 400 1.52588e-05C314 1.52399e-05 257 36.9575 176 27.4097C95 17.862 62 1.74982e-05 -8.74228e-06 1.74846e-05L-2.62268e-07 194C-1.17421e-07 197.314 2.68629 200 6 200L394 200Z' fill='${b}' fill-opacity='0.15'/%3E%3Cpath d='M6 200C2.68629 200 4.3986e-06 197.314 4.15839e-06 194L-6.23024e-06 50.6907C171 0.294407 211 109.677 400 50.696L400 194C400 197.314 397.314 200 394 200L6 200Z' fill='${b}' fill-opacity='0.3'/%3E%3Cpath d='M394 200C397.314 200 400 197.314 400 194L400 94.4844C250 68.8732 167 129.826 -4.61224e-06 94.4844L-2.62268e-07 194C-1.17422e-07 197.314 2.68629 200 6 200L394 200Z' fill='${b}' fill-opacity='0.5'/%3E%3Cpath d='M0 200L-2.60275e-06 134.751C174 102.111 181 186.391 400 134.931L400 200L0 200Z' fill='${b}'/%3E%3C/svg%3E%0A");
    `;
  },
  ({ preview, theme }) =>
    preview &&
    css`
      &:before {
        display: block;
        margin: -3rem -3rem 3rem -3rem;
        padding: 1rem;

        color: ${toColor(theme.church.buttonTextColor)};
        text-align: center;

        background-color: ${toColor(theme.church.buttonColor)};
        content: 'Preview Mode: Currently viewing unpublished class changes.';
      }
    `,
  mq({
    backgroundSize: ['75%', undefined, undefined, 'auto'],
    padding: ['3rem 3rem 15rem', undefined, undefined, '3rem 3rem 20rem'],
  })
);

export const FileBoxStyled = styled.div(
  ({ theme }) => ({ 'a, button': theme.font('sm') }),
  ({ theme }) => css`
    margin: 0.8rem 0;
    padding: 2.2rem 2.4rem;

    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.border};
    border-radius: ${theme.radius.md};
    a,
    button {
      display: flex;
      align-items: center;
      float: right;

      color: ${theme.colors.grayMed};
      text-decoration: underline;
      i {
        margin-left: 1.4rem;

        color: ${theme.colors.black};
      }
    }

    label {
      display: block;
      margin: -1.2rem -1.4rem;
      padding: 1rem;

      color: ${toColor(theme.church.buttonTextColor)};
      text-align: center;

      background: ${toColor(theme.church.buttonColor)};
      border-radius: ${theme.radius.md};
      cursor: pointer;
    }
  `
);
